/**
 * @module UseClusterBriefing
 * @purpose Hooks for fetching Part C AI briefings for clusters.
 *   Handles 503 (AI unavailable) as a non-error fallback path; exposes a
 *   non-blocking batch warm for the briefing board.
 * @depends services/reasoning-ai, services/reasoning, types/reasoning
 * @dependedOnBy pages/case/analysis/reasoning/briefing/BriefingBoardView,
 *   pages/case/analysis/reasoning/briefing/BriefingTile
 *
 * @rules
 * - Performance: stale-request requestId guard on useClusterBriefing;
 *     useBriefingBoard list fetch and batch warm are independent (first paint
 *     must NOT wait on the warm); summaries map mirrors headlines map lifecycle
 * - State: local useState per hook; no shared context
 * - Errors: 503 → aiUnavailable:true, error stays null; other errors → error string
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { getClusterAiSummary, batchClusterAiSummaries } from '../services/reasoning-ai'
import { listClusters } from '../services/reasoning'
import { ApiError } from '../services/api'
import type { ClusterBriefing, ClusterSummary } from '../types/reasoning'

const DEFAULT_TOP_N = 20
const OFFSET_ZERO = 0

// =============================================================================
// Module-level helpers — reduce callback complexity
// =============================================================================

function is503(err: unknown): boolean {
  return err instanceof ApiError && err.isServiceUnavailable
}

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function buildHeadlineMap(summaries: { clusterId: string; briefing: ClusterBriefing }[]): Map<string, string> {
  const map = new Map<string, string>()
  for (const summary of summaries) {
    map.set(summary.clusterId, summary.briefing.headline)
  }
  return map
}

function buildSummaryMap(summaries: { clusterId: string; briefing: ClusterBriefing }[]): Map<string, string> {
  const map = new Map<string, string>()
  for (const summary of summaries) {
    map.set(summary.clusterId, summary.briefing.summary)
  }
  return map
}

function buildBriefingMap(
  summaries: { clusterId: string; briefing: ClusterBriefing }[],
): Map<string, ClusterBriefing> {
  const map = new Map<string, ClusterBriefing>()
  for (const summary of summaries) {
    map.set(summary.clusterId, summary.briefing)
  }
  return map
}

// =============================================================================
// useClusterBriefing
// =============================================================================

interface BriefingSetters {
  setBriefing: (v: ClusterBriefing | null) => void
  setAiUnavailable: (v: boolean) => void
  setError: (v: string | null) => void
}

function applyBriefingError(err: unknown, setters: BriefingSetters): void {
  if (is503(err)) {
    setters.setBriefing(null)
    setters.setAiUnavailable(true)
  } else {
    setters.setError(messageOf(err, 'Failed to fetch AI briefing'))
    setters.setBriefing(null)
  }
}

/**
 * Fetch the Part C AI briefing for a single cluster.
 *
 * @behaviour Lazy: only fetches when all three IDs are non-null.
 *   503 responses set aiUnavailable:true and leave briefing null — the caller
 *   renders the deterministic fallback; this is NOT surfaced as an error.
 *   Stale-request guard (requestId ref) prevents out-of-order state writes when
 *   clusterId changes quickly.
 * @errors Sets error string for non-503 failures; clears on next fetch attempt.
 */
export function useClusterBriefing(
  caseId: string | null,
  runId: string | null,
  clusterId: string | null,
): {
  briefing: ClusterBriefing | null
  aiUnavailable: boolean
  loading: boolean
  error: string | null
} {
  const [briefing, setBriefing] = useState<ClusterBriefing | null>(null)
  const [aiUnavailable, setAiUnavailable] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const requestIdRef = useRef(0)

  const fetch = useCallback(async () => {
    if (!caseId || !runId || !clusterId) {
      setBriefing(null)
      setAiUnavailable(false)
      setError(null)
      setLoading(false)
      return
    }

    requestIdRef.current += 1
    const thisId = requestIdRef.current

    setLoading(true)
    setError(null)
    setAiUnavailable(false)

    try {
      const summary = await getClusterAiSummary(caseId, runId, clusterId)
      if (thisId !== requestIdRef.current) return
      setBriefing(summary.briefing)
    } catch (err) {
      if (thisId !== requestIdRef.current) return
      applyBriefingError(err, { setBriefing, setAiUnavailable, setError })
    } finally {
      if (thisId === requestIdRef.current) setLoading(false)
    }
  }, [caseId, runId, clusterId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { briefing, aiUnavailable, loading, error }
}

// =============================================================================
// useBriefingBoard — warm helpers
// =============================================================================

interface WarmParams {
  caseId: string
  runId: string
  topN: number
  thisId: number
  setHeadlines: (v: Map<string, string>) => void
  setSummaries: (v: Map<string, string>) => void
  setBriefings: (v: Map<string, ClusterBriefing>) => void
  setAiUnavailable: (v: boolean) => void
  setError: (v: string | null) => void
  setWarming: (v: boolean) => void
  warmRequestIdRef: { current: number }
}

async function runWarm(params: WarmParams): Promise<void> {
  const {
    caseId, runId, topN, thisId,
    setHeadlines, setSummaries, setBriefings, setAiUnavailable, setError, setWarming, warmRequestIdRef,
  } = params
  try {
    const result = await batchClusterAiSummaries(caseId, runId, topN)
    if (thisId !== warmRequestIdRef.current) return
    setHeadlines(buildHeadlineMap(result.summaries))
    setSummaries(buildSummaryMap(result.summaries))
    setBriefings(buildBriefingMap(result.summaries))
  } catch (err) {
    if (thisId !== warmRequestIdRef.current) return
    if (is503(err)) {
      setAiUnavailable(true)
    } else {
      setError(messageOf(err, 'Failed to warm AI headlines'))
    }
  } finally {
    if (thisId === warmRequestIdRef.current) setWarming(false)
  }
}

// =============================================================================
// useBriefingBoard
// =============================================================================

/**
 * Fetch the top-N clusters and concurrently batch-warm their AI headlines and summaries.
 *
 * @behaviour The cluster list fetch and the batch warm are independent: first
 *   paint renders tiles from clusters immediately with deterministic fallback
 *   headlines.  When the warm resolves, headlines and summaries Maps are populated
 *   and tiles can swap in AI headlines and show the prose summary paragraph.
 *   On batch 503 → aiUnavailable:true, both maps stay empty (all tiles fallback).
 *   loading tracks the list fetch; warming tracks the batch warm separately.
 * @errors Sets error string on list-fetch failure; batch warm failures set
 *   aiUnavailable or error depending on status code.
 */
interface UseBriefingBoardResult {
  clusters: ClusterSummary[]
  total: number
  headlines: Map<string, string>
  summaries: Map<string, string>
  briefings: Map<string, ClusterBriefing>
  aiUnavailable: boolean
  loading: boolean
  warming: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useBriefingBoard(
  caseId: string | null,
  runId: string | null,
  topN: number = DEFAULT_TOP_N,
): UseBriefingBoardResult {
  const [clusters, setClusters] = useState<ClusterSummary[]>([])
  const [total, setTotal] = useState(0)
  const [headlines, setHeadlines] = useState<Map<string, string>>(new Map())
  const [summaries, setSummaries] = useState<Map<string, string>>(new Map())
  const [briefings, setBriefings] = useState<Map<string, ClusterBriefing>>(new Map())
  const [aiUnavailable, setAiUnavailable] = useState(false)
  const [loading, setLoading] = useState(false)
  const [warming, setWarming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const listRequestIdRef = useRef(0)
  const warmRequestIdRef = useRef(0)

  const warmHeadlines = useCallback(async () => {
    if (!caseId || !runId) return

    warmRequestIdRef.current += 1
    const thisId = warmRequestIdRef.current

    setWarming(true)
    setHeadlines(new Map())
    setSummaries(new Map())
    setBriefings(new Map())
    setAiUnavailable(false)

    await runWarm({
      caseId, runId, topN, thisId,
      setHeadlines, setSummaries, setBriefings, setAiUnavailable, setError, setWarming, warmRequestIdRef,
    })
  }, [caseId, runId, topN])

  const fetchClusters = useCallback(async () => {
    if (!caseId || !runId) {
      setClusters([])
      setTotal(0)
      setError(null)
      setLoading(false)
      return
    }

    listRequestIdRef.current += 1
    const thisId = listRequestIdRef.current

    setLoading(true)
    setError(null)

    try {
      const page = await listClusters(caseId, runId, { limit: topN, offset: OFFSET_ZERO })
      if (thisId !== listRequestIdRef.current) return
      setClusters(page.clusters)
      setTotal(page.total)
    } catch (err) {
      if (thisId !== listRequestIdRef.current) return
      setError(messageOf(err, 'Failed to fetch clusters'))
      setClusters([])
      setTotal(0)
    } finally {
      if (thisId === listRequestIdRef.current) setLoading(false)
    }
  }, [caseId, runId, topN])

  const refetch = useCallback(async () => {
    await Promise.all([fetchClusters(), warmHeadlines()])
  }, [fetchClusters, warmHeadlines])

  useEffect(() => {
    void fetchClusters()
    void warmHeadlines()
  }, [fetchClusters, warmHeadlines])

  return { clusters, total, headlines, summaries, briefings, aiUnavailable, loading, warming, error, refetch }
}
